import { Text, TextProps, Box, BoxProps } from '@chakra-ui/react';
import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';

type AutoFitTextProps = {
  children: React.ReactNode;
  minFontSize?: number;
  maxFontSize?: number;
  containerHeight?: number | string;
  verticalAlign?: 'top' | 'center' | 'bottom';
} & Omit<TextProps, 'fontSize'>;

export const AutoFitText: React.FC<AutoFitTextProps> = ({
  children,
  minFontSize = 8,
  maxFontSize = 14,
  containerHeight,
  verticalAlign = 'bottom',
  ...textProps
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);
  const [shouldTruncate, setShouldTruncate] = useState(false);

  const content = children ?? '';

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const containerEl = containerRef.current;
    const textEl = textRef.current;

    if (!containerEl || !textEl) return;

    textEl.style.fontSize = `${maxFontSize}px`;

    let low = minFontSize;
    let high = maxFontSize;
    let best = minFontSize;

    for (let i = 0; i < 6; i++) {
      const mid = (low + high) / 2;
      textEl.style.fontSize = `${mid}px`;

      const fits =
        textEl.scrollWidth <= containerEl.clientWidth &&
        textEl.scrollHeight <= containerEl.clientHeight;

      if (fits) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    textEl.style.fontSize = `${best}px`;
    setFontSize(best);

    const stillTooBig =
      textEl.scrollWidth > containerEl.clientWidth ||
      textEl.scrollHeight > containerEl.clientHeight;

    setShouldTruncate(stillTooBig);
  }, [content, minFontSize, maxFontSize]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = () => setFontSize((f) => f + 0.01);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const { textAlign, ...restTextProps } = textProps;

  const alignItems =
    verticalAlign === 'top'
      ? 'flex-start'
      : verticalAlign === 'center'
        ? 'center'
        : 'flex-end';

  let justifyContent: BoxProps['justifyContent'] = 'flex-start';
  if (textAlign === 'right') justifyContent = 'flex-end';
  else if (textAlign === 'center') justifyContent = 'center';

  return (
    <Box
      ref={containerRef}
      overflow="hidden"
      whiteSpace="nowrap"
      width="100%"
      display="flex"
      alignItems={alignItems}
      justifyContent={justifyContent}
      height={containerHeight}
    >
      <Text
        ref={textRef as any}
        fontSize={fontSize}
        lineHeight="1.1"
        isTruncated={shouldTruncate}
        textAlign={textAlign}
        {...restTextProps}
      >
        {children}
      </Text>
    </Box>
  );
};
